import { useState } from 'react'
import { InfoTooltip } from '../../components/InfoTooltip'
import { CyclesTable, InfoTooltipLabel as ColLabel } from './CyclesTable'
import { SignalsCycleChart } from './SignalsCycleChart'
import { DASHBOARD_INFO } from './metricInfo'
import { formatSessionDateTime, sessionChip, toIndex100 } from './signalsModel'
import type { UnifiedSession } from './sessionsModel'

/** Cuántas filas se ven de entrada. El resto se pide con "Ver más": la lista
 *  NO scrollea adentro de su card (antes lo hacía y escondía el historial
 *  detrás de un scroll de 44px). */
const PAGE = 6

/**
 * Grilla compartida por la cabecera y todas las filas — mismo recurso que el
 * ranking (`RankingCard`): columnas fijas para los números, así todo queda
 * alineado. En mobile sólo entran fecha + tiros + goles + score; "Señal" e
 * "Índice" se esconden ahí y viajan al detalle de la fila.
 *
 * No es una `<table>` a propósito: el panel expandido lleva adentro la tabla
 * de ciclos, que es ancha, y dentro de un `<table>` eso estira la tabla
 * entera y deja media fila fuera de pantalla en mobile.
 */
const GRID =
  'grid grid-cols-[minmax(0,1fr)_2.6rem_2.6rem_3.2rem] sm:grid-cols-[minmax(0,1fr)_7rem_3rem_3rem_3.6rem_3.6rem] items-center gap-x-md'

const NUM = 'text-right font-mono text-sm tabular-nums whitespace-nowrap'

/**
 * LA lista de sesiones de `/data` — una sola, desde el 2026-09-07. Antes
 * había dos tablas con las mismas sesiones: "Sesiones recientes" (resultado
 * de juego) y "Todas tus sesiones" (señales). Acá cada sesión es UNA fila y
 * la fila se abre con todo lo suyo: resultado, chequeo previo de esa sesión y
 * sus ciclos de regulación.
 */
export function SessionsSection({ sessions }: { sessions: UnifiedSession[] | null }) {
  const [shown, setShown] = useState(PAGE)
  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <section id="sesiones">
      <div className="mb-md flex items-center gap-xs">
        <h2 className="m-0">Sesiones</h2>
        <InfoTooltip label="Estados de sesión">{DASHBOARD_INFO.section_states}</InfoTooltip>
      </div>

      <div className="card border-primary/30">
        {sessions === null ? (
          <p className="m-0 text-sm text-text-muted">Cargando…</p>
        ) : sessions.length === 0 ? (
          <p className="m-0 text-sm text-text-muted">
            Todavía no entrenaste. Tus sesiones van a aparecer acá.
          </p>
        ) : (
          <>
            <div
              className={`${GRID} border-b border-outline pb-xs text-xs uppercase tracking-wide text-text-muted`}
            >
              <span>Fecha</span>
              <span className="hidden sm:block">
                <ColLabel label="Señal" info={DASHBOARD_INFO.section_states} />
              </span>
              <span className="text-right">Tiros</span>
              <span className="text-right">Goles</span>
              <span className="text-right">Score</span>
              <span className="hidden justify-end sm:flex">
                <ColLabel label="Índice" info={DASHBOARD_INFO.kpi_last_index} />
              </span>
            </div>

            <ul className="m-0 flex list-none flex-col p-0">
              {sessions.slice(0, shown).map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  open={expanded === s.id}
                  onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                />
              ))}
            </ul>

            {sessions.length > shown && (
              <button
                type="button"
                className="btn mt-md text-xs"
                onClick={() => setShown((n) => n + PAGE)}
              >
                Ver más ({sessions.length - shown})
              </button>
            )}
          </>
        )}
      </div>
    </section>
  )
}

function SessionRow({
  session,
  open,
  onToggle,
}: {
  session: UnifiedSession
  open: boolean
  onToggle: () => void
}) {
  const signals = session.signals
  const chip = signals ? sessionChip(signals) : null
  const play = session.play

  return (
    <li className="border-b border-outline/60 last:border-b-0">
      {/* La fila entera es el disparador: en mobile no entra una columna
          extra sólo para un link "detalle". */}
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={`${GRID} w-full py-sm text-left hover:bg-surface-highlight/20`}
      >
        <span className="flex min-w-0 items-center gap-xs text-sm text-text-primary">
          <svg
            aria-hidden
            viewBox="0 0 12 12"
            className={`h-3 w-3 shrink-0 text-secondary transition-transform ${
              open ? 'rotate-90' : ''
            }`}
          >
            <path
              d="M4.5 2 8.5 6 4.5 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="truncate">{formatSessionDateTime(session.startedAt)}</span>
        </span>
        <span className="hidden sm:block">
          {chip ? (
            <span className={chip.className} title={chip.help}>
              {chip.label}
            </span>
          ) : (
            <span className="text-xs text-text-muted">—</span>
          )}
        </span>
        <span className={NUM}>{session.nTrials}</span>
        <span className={NUM}>{play ? play.goals : '—'}</span>
        <span className={NUM}>{play ? play.score.toFixed(2) : '—'}</span>
        <span className={`hidden sm:block ${NUM}`}>
          {toIndex100(signals?.meanRCardio ?? null) ?? '—'}
        </span>
      </button>

      {open && <SessionDetail session={session} />}
    </li>
  )
}

function SessionDetail({ session }: { session: UnifiedSession }) {
  const { play, signals } = session
  const cycles = signals?.cycles ?? []
  const goalRate =
    play && play.n_trials > 0 ? Math.round((play.goals / play.n_trials) * 100) : null

  return (
    <div className="mb-sm flex flex-col gap-md rounded-md bg-surface-highlight/20 p-md">
      <dl className="grid grid-cols-2 gap-md sm:grid-cols-4 lg:grid-cols-6">
        <Fact label="Señal" value={signals ? sessionChip(signals).label : '—'} />
        <Fact label="Atajadas" value={play ? String(play.saves) : '—'} />
        <Fact label="Fallos" value={play ? String(play.misses) : '—'} />
        <Fact label="Efectividad" value={goalRate !== null ? `${goalRate}%` : '—'} />
        <Fact
          label="Índice medio"
          value={toIndex100(signals?.meanRCardio ?? null)?.toString() ?? '—'}
        />
        <Fact
          label="Mejor ciclo"
          value={toIndex100(signals?.bestRCardio ?? null)?.toString() ?? '—'}
        />
        <Fact
          label="Ciclos medidos"
          value={cycles.length > 0 ? `${signals!.measuredCycles}/${cycles.length}` : '—'}
        />
        <Fact
          label="Chequeo previo"
          value={
            session.checkInAt
              ? `Respondido ${session.checkInAt.toLocaleDateString('es-AR')}`
              : 'Sin chequeo'
          }
        />
        <Fact
          label="Calidad de sync"
          value={SYNC_LABELS[play?.sync_quality ?? ''] ?? '—'}
        />
      </dl>

      {cycles.length > 0 ? (
        <>
          <SignalsCycleChart cycles={cycles} />
          <CyclesTable cycles={cycles} />
        </>
      ) : (
        <p className="m-0 text-xs text-text-muted">
          {signals ? sessionChip(signals).help : 'Sin datos de señales para esta sesión.'}
        </p>
      )}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="m-0 mt-xs text-sm font-medium text-text-primary">{value}</dd>
    </div>
  )
}

const SYNC_LABELS: Record<string, string> = {
  complete: 'Completa',
  partial: 'Parcial',
  degraded: 'Incompleta',
  no_data: 'Sin datos',
}
